/**
 * Employee performance, this period against the comparable one.
 *
 * The screen is built around one refusal: it will not reduce a person to a
 * single "performing / not performing" verdict, because that verdict is two
 * questions in a trench coat. Where someone stands against their peers and
 * which way they are moving are different facts, they disagree constantly, and
 * the disagreement is the useful part. A top-quartile manager down 40% is the
 * most urgent row here and a plain league table shows them at the top with a
 * tick beside their name.
 *
 * So bands come from the pair — Star, Slipping, Improving, Watch — and the
 * band strip is also the filter, because the first thing anyone wants after
 * seeing "43 slipping" is those 43 rows.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDownRight, ArrowRight, ArrowUpRight, Info, Timer,
  Users,
} from "lucide-react";

import { api } from "@/api/client";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import type { Metadata, Quality } from "@/types/api";
import { count, inr } from "@/utils/format";

type Window = "month" | "quarter" | "year";
type Basis = "to_date" | "complete";

const WINDOWS: { key: Window; label: string; hint: string }[] = [
  { key: "month", label: "Month", hint: "This month against last month" },
  { key: "quarter", label: "Quarter", hint: "This financial quarter against last" },
  { key: "year", label: "Year", hint: "This financial year against last" },
];

const BAND_TONE: Record<string, { color: string; bg: string }> = {
  good: { color: "#2F6B3C", bg: "#E7F1E9" },
  warn: { color: "#8A6410", bg: "#FBF3E0" },
  bad: { color: "#9E3B2E", bg: "#FBECE9" },
  neutral: { color: "#6B6459", bg: "#F0EEEA" },
};

/** Falls back to neutral so a tone the backend adds later still renders. */
const tone = (key: string) => BAND_TONE[key] ?? BAND_TONE.neutral!;

interface Band {
  key: string; label: string; meaning: string; tone: string; count: number;
}

interface Row {
  person_key: string;
  name: string;
  designation: string;
  branch: string;
  value: number;
  previous_value: number;
  delta: number;
  delta_pct: number | null;
  trend: string;
  percentile: number | null;
  quartile: number | null;
  rankable: boolean;
  band: string;
  band_label: string;
  band_tone: string;
  comparable: boolean;
  not_comparable_reason: string;
  consistency: number | null;
  momentum_pct: number | null;
  top_client_share_pct: number | null;
  revenue_per_activity: number | null;
  response_rate_pct: number | null;
  identity_confident: boolean;
  trend_12m: number[];
  current: Record<string, number>;
  previous: Record<string, number>;
}

interface Payload {
  filters: {
    window: Window; basis: Basis; metric: string;
    measured_to: string; today: string;
    maturity: { detected: boolean; days?: number; reason: string };
    windows: {
      current: { start: string; end: string; label: string; days: number };
      previous: { start: string; end: string; label: string; days: number };
      current_is_partial: boolean;
      equal_length: boolean;
      note: string;
    };
  };
  summary: {
    metric: string; metric_label: string; metric_unit: string;
    employees: number; comparable: number;
    improved: number; declined: number; flat: number;
    current_total: number; previous_total: number;
    total_delta_pct: number | null; median_delta_pct: number | null;
    cohort: { n: number; p25: number | null; median: number | null;
              p75: number | null; top: number | null };
    concentration_gini: number | null;
    top_10pct_share: number | null;
    active_now: number;
    active_previous: number;
    headcount_delta_pct: number | null;
    per_head_now: number | null;
    per_head_previous: number | null;
    per_head_delta_pct: number | null;
    bands: Band[];
    available_metrics: Record<string, string>;
    flat_band_pct: number;
  };
  breakdown: Row[];
  quality?: Quality;
  metadata?: Metadata;
}

export function PerformanceComparison() {
  const navigate = useNavigate();
  const [window, setWindow] = useState<Window>("month");
  const [basis, setBasis] = useState<Basis>("to_date");
  const [metric, setMetric] = useState("revenue");
  const [band, setBand] = useState("");
  const [sort, setSort] = useState("-delta_pct");

  const { data, isLoading } = useQuery({
    queryKey: ["employee-comparison", window, basis, metric, band, sort],
    queryFn: async () => {
      const { data } = await api.get<Payload>("/analytics/employees/comparison", {
        params: { window, basis, metric, band: band || undefined, sort },
      });
      return data;
    },
    staleTime: 120_000,
  });

  const rows = data?.breakdown ?? [];
  const summary = data?.summary;
  const windows = data?.filters?.windows;
  const maturity = data?.filters?.maturity;
  const isMoney = summary?.metric_unit === "inr";
  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined ? "—" : isMoney ? inr(v) : count(v);

  const columns = useMemo<Column<Row>[]>(() => [
    {
      key: "name", header: "Employee", width: "210px",
      render: (r) => (
        <span className="min-w-0">
          <span className="font-medium truncate block">{r.name}</span>
          <span className="text-[11px] text-ink-faint truncate block">
            {r.designation || "designation not set"}
            {r.branch ? ` · ${r.branch}` : ""}
          </span>
        </span>
      ),
    },
    {
      key: "band", header: "Band", width: "140px",
      render: (r) => (
        <span className="chip border-transparent" title={r.not_comparable_reason}
              style={tone(r.band_tone)}>
          {r.band_label}
        </span>
      ),
    },
    {
      key: "previous_value", header: windows?.previous.label ?? "Previous",
      align: "right", render: (r) => fmt(r.previous_value),
      value: (r) => r.previous_value,
    },
    {
      key: "value", header: windows?.current.label ?? "Current",
      align: "right",
      render: (r) => <span className="font-medium">{fmt(r.value)}</span>,
      value: (r) => r.value,
    },
    {
      key: "delta_pct", header: "Change", align: "right", width: "120px",
      render: (r) => <DeltaCell pct={r.delta_pct} flat={summary?.flat_band_pct ?? 10} />,
      value: (r) => r.delta_pct,
    },
    {
      key: "trend_12m", header: "12 months", width: "104px",
      value: () => null,
      render: (r) => <Sparkline values={r.trend_12m} />,
    },
    {
      key: "percentile", header: "Vs peers", align: "right", width: "118px",
      render: (r) => (r.percentile === null
        ? <span className="text-ink-faint text-[11.5px]"
                title={r.not_comparable_reason || "too few activities to rank"}>
            not ranked
          </span>
        : <PercentileCell percentile={r.percentile} quartile={r.quartile} />),
      value: (r) => r.percentile,
    },
    {
      key: "momentum_pct", header: "Momentum", align: "right", hidden: true,
      render: (r) => (r.momentum_pct === null ? <span className="text-ink-faint">—</span>
        : <span className={r.momentum_pct >= 0 ? "text-good" : "text-bad"}>
            {r.momentum_pct > 0 ? "+" : ""}{r.momentum_pct}%/mo
          </span>),
      value: (r) => r.momentum_pct,
    },
    {
      key: "consistency", header: "Consistency", align: "right", hidden: true,
      render: (r) => (r.consistency === null
        ? <span className="text-ink-faint" title="fewer than 3 active months">—</span>
        : <span className="tnum">{r.consistency.toFixed(0)}</span>),
      value: (r) => r.consistency,
    },
    {
      key: "top_client_share_pct", header: "Top client", align: "right", hidden: true,
      render: (r) => (r.top_client_share_pct === null
        ? <span className="text-ink-faint">—</span>
        : <span className={r.top_client_share_pct >= 70 ? "text-warn" : ""}
                title="Share of this period's revenue from their single biggest client">
            {r.top_client_share_pct}%
          </span>),
      value: (r) => r.top_client_share_pct,
    },
    {
      key: "activities", header: "Activities", align: "right",
      render: (r) => (
        <span className="tnum" title="Effort, for comparison against the money column">
          {count(r.current.activities)}
        </span>
      ),
      value: (r) => r.current.activities ?? null,
    },
  ], [windows?.current.label, windows?.previous.label, isMoney,
      summary?.flat_band_pct]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">
          Performance comparison
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Where each person stands against their peers, and which way they have
          moved. The two disagree often — and a strong performer who is falling
          is a more urgent conversation than a weak one who always was.
        </p>
      </div>

      {/* ---- controls ---- */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <span className="label">Compare</span>
          <div className="flex items-center gap-1 bg-surface-alt rounded-md p-0.5">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWindow(w.key)} title={w.hint}
                      className={`px-3 py-1 rounded text-[12.5px] ${
                        window === w.key
                          ? "bg-surface shadow-card font-medium text-ink"
                          : "text-ink-muted hover:text-ink"}`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="label">Window</span>
          <div className="flex items-center gap-1 bg-surface-alt rounded-md p-0.5">
            <button onClick={() => setBasis("to_date")}
                    title="Same elapsed days on both sides — includes the period in progress"
                    className={`px-3 py-1 rounded text-[12.5px] ${
                      basis === "to_date"
                        ? "bg-surface shadow-card font-medium text-ink"
                        : "text-ink-muted hover:text-ink"}`}>
              To date
            </button>
            <button onClick={() => setBasis("complete")}
                    title="The last two finished periods — nothing still moving"
                    className={`px-3 py-1 rounded text-[12.5px] ${
                      basis === "complete"
                        ? "bg-surface shadow-card font-medium text-ink"
                        : "text-ink-muted hover:text-ink"}`}>
              Complete
            </button>
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="label">Measure by</span>
          <select value={metric} onChange={(e) => setMetric(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            {Object.entries(summary?.available_metrics ?? { revenue: "Revenue" })
              .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="label">Rank by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="-delta_pct">Biggest rise</option>
            <option value="delta_pct">Biggest fall</option>
            <option value="-value">Highest now</option>
            <option value="-percentile">Peer rank</option>
            <option value="-momentum_pct">Momentum</option>
            <option value="consistency">Least consistent</option>
          </select>
        </label>
      </div>

      {/* ---- what is actually being compared ---- */}
      {windows && (
        <div className="card p-3.5 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-[13.5px]">
            <span className="font-medium">{windows.previous.label}</span>
            <ArrowRight size={14} className="text-ink-faint" />
            <span className="font-medium">{windows.current.label}</span>
            <span className="text-[11.5px] text-ink-faint">
              {windows.previous.days}d vs {windows.current.days}d
            </span>
          </div>
          <p className="text-[12px] text-ink-muted">{windows.note}</p>
          {!windows.equal_length && (
            <p className="flex items-start gap-1.5 text-[12px] text-warn">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              The two sides are different lengths, so the change is not strictly
              like for like.
            </p>
          )}
          {maturity?.detected && (
            <p className="flex items-start gap-1.5 text-[12px] text-ink-soft
                          bg-surface-alt rounded-md px-2.5 py-2">
              <Timer size={12} className="mt-0.5 shrink-0 text-petrol" />
              <span>
                <strong>Measured to {data!.filters.measured_to}</strong>, not
                today. {maturity.reason}
              </span>
            </p>
          )}
        </div>
      )}

      {/* ---- headline ---- */}
      <div className="grid gap-3"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label={`${summary?.metric_label ?? "Total"} now`}
                 value={fmt(summary?.current_total)}
                 previous={fmt(summary?.previous_total)}
                 deltaPct={summary?.total_delta_pct ?? null} />
        <KpiCard loading={isLoading} label="Improved"
                 value={count(summary?.improved)} />
        <KpiCard loading={isLoading} label="Declined"
                 value={count(summary?.declined)} higherIsBetter={false} />
        <KpiCard loading={isLoading} label="Typical change"
                 value={summary?.median_delta_pct === null
                   || summary?.median_delta_pct === undefined
                   ? "—"
                   : `${summary.median_delta_pct > 0 ? "+" : ""}${summary.median_delta_pct}%`} />
        {/* The total and the per-person figure can point opposite ways when
            headcount moves, and only one of them is about performance. */}
        <KpiCard loading={isLoading} label="Per active person"
                 value={fmt(summary?.per_head_now)}
                 previous={fmt(summary?.per_head_previous)}
                 deltaPct={summary?.per_head_delta_pct ?? null}
                 footnote={summary
                   ? `${summary.active_previous} → ${summary.active_now} people active`
                   : undefined} />
      </div>

      {summary?.headcount_delta_pct !== null
        && summary?.headcount_delta_pct !== undefined
        && Math.abs(summary.headcount_delta_pct) >= 10
        && summary.total_delta_pct !== null
        && summary.per_head_delta_pct !== null && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-md border
                        border-rule bg-surface-alt text-[12.5px]">
          <Users size={14} className="mt-0.5 shrink-0 text-petrol" />
          <span>
            Active headcount moved{" "}
            <strong>{summary.headcount_delta_pct > 0 ? "+" : ""}
              {summary.headcount_delta_pct}%</strong>{" "}
            ({summary.active_previous} → {summary.active_now}).
            {Math.sign(summary.total_delta_pct) !== Math.sign(summary.per_head_delta_pct)
              ? <> The total and the per-person figure point in{" "}
                  <strong>opposite directions</strong> — the total moved because
                  the team changed size, not because people changed output.</>
              : <> Read the per-person figure alongside the total.</>}
          </span>
        </div>
      )}

      {/* ---- bands, which are also the filter ---- */}
      {summary && (
        <div className="card p-4 space-y-2.5">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="label">performance bands</span>
            <span className="text-[12px] text-ink-faint">
              level against peers × direction since {windows?.previous.label}
            </span>
            {band && (
              <button onClick={() => setBand("")}
                      className="text-[11.5px] text-petrol underline ml-auto">
                show all bands
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.bands.map((b) => (
              <button
                key={b.key}
                onClick={() => setBand(band === b.key ? "" : b.key)}
                disabled={b.count === 0}
                title={b.meaning}
                className={`flex-1 min-w-[136px] text-left rounded-md border px-3 py-2
                            transition-opacity ${b.count === 0 ? "opacity-40" : ""}
                            ${band && band !== b.key ? "opacity-45" : ""}`}
                style={{
                  borderColor: `${tone(b.tone).color}44`,
                  background: band === b.key ? tone(b.tone).bg : "transparent",
                }}
              >
                <span className="block font-display font-bold text-[19px] tnum"
                      style={{ color: tone(b.tone).color }}>
                  {b.count}
                </span>
                <span className="block text-[12.5px] font-medium">{b.label}</span>
                <span className="block text-[11px] text-ink-faint leading-snug mt-0.5">
                  {b.meaning}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ---- cohort shape ---- */}
      {summary?.cohort?.n ? (
        <div className="card p-4">
          <div className="label mb-2">how the team is distributed</div>
          <div className="grid gap-3"
               style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))" }}>
            <Stat label="Bottom quartile" value={fmt(summary.cohort.p25)}
                  hint="25% of ranked people are at or below this" />
            <Stat label="Median" value={fmt(summary.cohort.median)} />
            <Stat label="Top quartile" value={fmt(summary.cohort.p75)}
                  hint="25% are at or above this" />
            <Stat label="Best" value={fmt(summary.cohort.top)} />
            <Stat
              label="Carried by the top 10%"
              value={summary.top_10pct_share === null ? "—" : `${summary.top_10pct_share}%`}
              hint="Share of the total produced by the top tenth. High means the team depends on a few people."
              warn={(summary.top_10pct_share ?? 0) >= 40}
            />
          </div>
          <p className="text-[11.5px] text-ink-faint mt-2.5">
            Ranked cohort is {summary.cohort.n} of {summary.employees} people.
            The rest joined or left mid-window, or have too little activity to
            rank — they are listed below but excluded from these figures.
          </p>
        </div>
      ) : null}

      <DataTable<Row>
        rows={rows}
        columns={columns}
        rowKey={(r) => r.person_key}
        onRowClick={(r) => navigate(`/employees/${encodeURIComponent(r.person_key)}`)}
        exportName={`performance-${window}-${metric}`}
        loading={isLoading}
        emptyMessage="No employees in this band for this window."
        footnote="Click a row for that person's profile and journey. Hidden columns — momentum, consistency, top-client share — are available from the column picker."
      />

      <p className="flex items-start gap-2 text-[12px] text-ink-faint">
        <Info size={13} className="mt-0.5 shrink-0" />
        Revenue and commission are dated when finance approved them, so a jump
        can be a cleared backlog rather than work done. The Activities column is
        recorded on the day it happened — compare the two before concluding
        anything about one person.
      </p>

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function Stat({ label, value, hint, warn }: {
  label: string; value: string; hint?: string; warn?: boolean;
}) {
  return (
    <div title={hint}>
      <div className="label">{label}</div>
      <div className={`font-display font-bold text-[17px] tnum mt-0.5 ${
        warn ? "text-warn" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function DeltaCell({ pct, flat }: { pct: number | null; flat: number }) {
  if (pct === null) {
    return <span className="text-ink-faint text-[11.5px]" title="nothing in the
                  previous period to compare against">no basis</span>;
  }
  const rising = pct > flat;
  const falling = pct < -flat;
  const Icon = rising ? ArrowUpRight : falling ? ArrowDownRight : ArrowRight;
  const colour = rising ? "text-good" : falling ? "text-bad" : "text-ink-muted";
  return (
    <span className={`inline-flex items-center gap-1 justify-end tnum ${colour}`}>
      <Icon size={12} />
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
}

function PercentileCell({ percentile, quartile }: {
  percentile: number; quartile: number | null;
}) {
  const colour = quartile === 4 ? "#2F6B3C" : quartile === 3 ? "#2563C9"
    : quartile === 2 ? "#8A6410" : "#9E3B2E";
  return (
    <span className="inline-flex items-center gap-1.5 justify-end w-full"
          title={`Ahead of ${percentile}% of the ranked cohort (Q${quartile})`}>
      <span className="tnum text-[12px]" style={{ color: colour }}>
        Q{quartile}
      </span>
      <span className="w-10 h-1.5 rounded-full bg-surface-alt overflow-hidden shrink-0">
        <span className="block h-full rounded-full"
              style={{ width: `${percentile}%`, background: colour }} />
      </span>
    </span>
  );
}

/**
 * Twelve months of revenue as a bare sparkline.
 *
 * No axis on purpose — at this size a reader can only take shape from it, and
 * decorating it with numbers too small to read would imply a precision the
 * 104px width cannot carry. The row's own columns hold the actual figures.
 */
function Sparkline({ values }: { values: number[] }) {
  if (!values?.length || values.every((v) => v === 0)) {
    return <span className="text-ink-faint text-[11px]">no history</span>;
  }
  const width = 92;
  const height = 22;
  const max = Math.max(...values) || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - (v / max) * height).toFixed(1)}`)
    .join(" ");
  const last = values[values.length - 1] ?? 0;

  return (
    <svg width={width} height={height} className="overflow-visible"
         role="img" aria-label={`${values.length} month trend`}>
      <polyline points={points} fill="none" stroke="#7A8B86" strokeWidth={1.2}
                strokeLinejoin="round" />
      <circle cx={width} cy={height - (last / max) * height} r={2}
              fill={last >= max * 0.6 ? "#2F6B3C" : "#9E3B2E"} />
    </svg>
  );
}
